import React from 'react'

import cx from 'classnames'
import { TooltipFormatterContextObject } from 'highcharts'
import moment from 'moment'
import { map } from 'ramda'
import { renderToStaticMarkup } from 'react-dom/server'

import { PlaybookVersionResponse } from '@driftt/api-types'
import { Div, Span } from '@driftt/tide-core'

import { toPercentString } from 'api/report/realTime/helpers'
import { ExperimentFlowMetrics } from 'api/report/types'
import BotVariantIndicator from 'components/core/BotVariantIndicator'
import TrendIndicator, { TrendDirection } from 'components/core/TrendIndicator'
import { EmailCampaignMetricDisplayName } from 'components/email/PerformanceReport/EmailCampaignPerformanceChart/MetricOptions'
import { AggregationType } from 'components/revenue/RevenueReportTab/types'
import { METRIC_LABEL_MAP } from 'layouts/RealTimeDashboard/constants'
import { calcDelta, militaryToStandard, TypeFormats } from 'utils/format'
import { isNilOrEmpty } from 'utils/ramda'

import { LINE_CHART_NUMERIC_VALUE_METRICS } from '../LineChart/graph'

import TooltipDeltaLabel from './TooltipDeltaLabel'
import TooltipPointLabel from './TooltipPointLabel'
import TooltipVersionLabel from './TooltipVersionLabel'

const CURRENT_SERIES_INDEX = 0
const PREV_SERIES_INDEX = 1

const TOOLTIP_TITLES_PER_METRIC = Object.freeze({
  newConversations: 'New conversations',
  medianResponseTimeLine: 'Median response time',
  medianConversationLengthLine: 'Median conversation length',
  meetingsBookedByDate: 'Meetings booked',
  callsMadeByDate: 'Calls made',
  conversations: 'Conversations',
  leadsCaptured: 'Emails captured',
  meetingsBooked: 'Meetings booked',
  serviceDeflection: 'Deflection rate',
  ...EmailCampaignMetricDisplayName,
})

interface LineGraphToolTipFormatterOptions {
  invertDelta: boolean
  playbookVersionHistory?: PlaybookVersionResponse[]
}
export const lineGraphTooltipFormatter = function (
  metric,
  { invertDelta, playbookVersionHistory }: LineGraphToolTipFormatterOptions
) {
  const isServiceDeflection = metric === 'serviceDeflection'
  return function () {
    if (!this.points || this.points.length === 0) {
      return ''
    }

    const durationPoints = !LINE_CHART_NUMERIC_VALUE_METRICS.includes(metric)

    const points = map(
      (point) =>
        renderToStaticMarkup(
          <TooltipPointLabel
            point={point}
            durationPoints={durationPoints}
            metric={metric}
          />
        ),
      this.points
    )

    let deltaString = ''
    if (this.points.length >= 2) {
      const delta = calcDelta(
        this.points[CURRENT_SERIES_INDEX].y,
        this.points[PREV_SERIES_INDEX].y,
        invertDelta
      )
      deltaString = renderToStaticMarkup(<TooltipDeltaLabel delta={delta} />)
    }

    if (isServiceDeflection) {
      const point = this.points[CURRENT_SERIES_INDEX]
      const delta = calcDelta(
        point.y,
        point.series.yData[point.point.index - 1],
        invertDelta
      )
      deltaString = renderToStaticMarkup(<TooltipDeltaLabel delta={delta} />)
    }

    let versionHistoryString = ''
    if (playbookVersionHistory) {
      versionHistoryString = renderToStaticMarkup(
        <TooltipVersionLabel
          isoDate={this.points[CURRENT_SERIES_INDEX].key}
          versions={playbookVersionHistory}
        />
      )
    }
    // these are slightly different to renderToStaticMarkup
    return `
      <div class="tide-div highcharts-tooltip-container">
        <span class="tide-span highcharts-tooltip-header">${
          TOOLTIP_TITLES_PER_METRIC[metric]
        }</span>
        <div class="tide-div highcharts-tooltip-label-container">
          ${points.join('')}
          ${deltaString}
          ${versionHistoryString}
        </div>
      </div>`
  }
}

export const heatMapTooltipFormatter = function () {
  const { x, y, value } = this.point
  const { xAxis } = this.series
  return `
    <div class="tide-div highcharts-tooltip-container">
      <span class="tide-span highcharts-tooltip-header">New conversations</span>
      <div class="tide-div highcharts-tooltip-label-container">
        <span class="tide-span highcharts-tooltip-label">
          <p class="tide-paragraph tide-paragraph-1 highcharts-tooltip-label-key">
            ${xAxis.categories[x]}, ${militaryToStandard(y)}
          </p>
          <p>${value}</p>
        </span>
      </div>
    </div>`
}

export const barChartTooltipFormatter = function () {
  if (isNilOrEmpty(this?.points)) {
    return ''
  }

  const [pointA, pointB] = this.points

  return `
    <div class="tide-div highcharts-tooltip-container highcharts-tooltip-container--bar-chart">
      <div class="tide-div highcharts-tooltip-header">${pointA.key}</div>
      <div class="tide-div highcharts-tooltip-label-container site-traffic--container">
        <span class="tide-span highcharts-tooltip-label">
          <span class="site-traffic--path">Total visitors</span>
          <span class="site-traffic--visits">${TypeFormats.RAW_HUMANIZED(
            pointA.y
          )}</span>
        </span>
        <span class="tide-span highcharts-tooltip-label">
          <span class="site-traffic--path">Visitors shown a playbook</span>
          <span class="site-traffic--visits">${TypeFormats.RAW_HUMANIZED(
            pointB.y
          )}</span>
        </span>
      </div>
    </div>`
}

export const revenueBarChartTooltipFormatter = (agg: AggregationType) =>
  function () {
    if (isNilOrEmpty(this.points)) {
      return ''
    }
    const total = this.points.reduce(
      (last, cur: TooltipFormatterContextObject) => cur.y + last,
      0
    )
    const tips = this.points
      .map(
        (point: TooltipFormatterContextObject) =>
          `<span class="tide-span highcharts-tooltip-label">
            <div style="width:12px;height:12px;background:${point.color}"></div>
            <span class="revenue-tooltip-label">${point.series.name}</span>
            <span class="revenue-tooltip-spacer"></span>
            <span class="revenue-tooltip-amount">${
              agg === AggregationType.Opportunity
                ? TypeFormats.RAW_HUMANIZED(point.y ?? 0)
                : TypeFormats.CURRENCY(point.y ?? 0)
            }</span>
          </span>`
      )
      .reduce((a, b) => a + b, '')

    return `<div class="tide-div highcharts-tooltip-container highcharts-tooltip-container--bar-chart">
      <div class="tide-div highcharts-tooltip-header">${
        this.points[0].key
      }</div>
      <div class="tide-div highcharts-tooltip-label-container site-traffic--container">
      ${tips}
      <div class="highcharts-tooltip-label revenue-tooltip-total-container">
        <span class="revenue-tooltip-label">Total: </span>
        <span class="revenue-tooltip-amount">${
          agg === AggregationType.Opportunity
            ? TypeFormats.RAW_HUMANIZED(total)
            : TypeFormats.CURRENCY_UNROUNDED(total)
        }</span>
      </div>
      </div>
    </div>`
  }

export const audienceCoverageChartTooltipFormatter = function () {
  if (isNilOrEmpty(this.points)) {
    return ''
  }

  const [pointA, pointB] = this.points

  const pointATooltip =
    pointA !== undefined
      ? `<div class="tide-div highcharts-coverage-tooltip-label audience-coverage-tooltip--secondRowPoint">
  <span class="tide-span audience-coverage-tooltip--color" style="background-color:${
    pointA.color
  };"></span>
  <span class="tide-span highcharts-tooltip-label-key">Visitors not shown a playbook</span>
  <span class="tide-span audience-coverage-tooltip--value">${TypeFormats.RAW_HUMANIZED(
    pointA.y
  )}</span>
</div>`
      : ''

  const pointBTooltip =
    pointB !== undefined
      ? `        <div class="tide-div highcharts-coverage-tooltip-label">
      <span class="tide-span audience-coverage-tooltip--color" style="background-color: ${
        pointB.color
      }"></span>
      <span class="tide-span highcharts-tooltip-label-key">Visitors shown a playbook</span>
      <span class="tide-span audience-coverage-tooltip--value">${TypeFormats.RAW_HUMANIZED(
        pointB.y
      )}</span>
    </div>`
      : ''

  return `
    <div class="tide-div highcharts-tooltip-container highcharts-tooltip-container--bar-chart">
      <div class="tide-div highcharts-tooltip-header">${
        pointB?.key || pointA?.key
      }</div>
      <div class="tide-div highcharts-tooltip-label-container">
        ${pointBTooltip}
        ${pointATooltip}
      </div>
    </div>
  `
}

export const salesCycleTooltipFormatter = () =>
  function () {
    if (isNilOrEmpty(this.points)) {
      return ''
    }
    const total = this.points.reduce(
      (last, cur: TooltipFormatterContextObject) => cur.y + last,
      0
    )

    return `<div class="tide-div highcharts-tooltip-container highcharts-tooltip-container--bar-chart">
      <div class="tide-div highcharts-tooltip-header">${
        this.points[0].key
      }</div>
      <div class="tide-div highcharts-tooltip-label-container site-traffic--container">

      <div class="highcharts-tooltip-label revenue-tooltip-total-container">
        <span class="revenue-tooltip-label">Total: </span>
        <span class="revenue-tooltip-amount">${TypeFormats.FIXED(
          total
        )} days</span>
      </div>
      </div>
    </div>`
  }

export const pieChartFormatter = function () {
  const { y } = this.point
  return `
      <div class="tide-div highcharts-tooltip-container">
        <div class="tide-div highcharts-tooltip-label-container">
          <span class="tide-span highcharts-tooltip-label">
            <p class="tide-paragraph tide-paragraph-1 highcharts-tooltip-label-key">
              ${this.key}
            </p>
            <p>${y}</p>
          </span>
        </div>
      </div>`
}

export const currencyLabelFormatter = function () {
  return TypeFormats.CURRENCY(this.value)
}

export const humanLabelFormatter = function () {
  return TypeFormats.RAW_HUMANIZED(this.value)
}

export const automationBarChartTooltipFormatter = function () {
  if (isNilOrEmpty(this.points)) {
    return ''
  }

  const [pointA] = this.points

  return `
    <div class="tide-div highcharts-tooltip-container highcharts-tooltip-container--bar-chart">
      <div class="tide-div highcharts-tooltip-header">${pointA.key}</div>
      <div class="tide-div highcharts-tooltip-label-container site-traffic--container">
        <span class="tide-span highcharts-tooltip-label">
          <span class="site-traffic--path">Mentions</span>
          <span class="site-traffic--visits">${TypeFormats.RAW_HUMANIZED(
            pointA.y
          )}</span>
        </span>
      </div>
    </div>`
}

export const teamAvailabilityBarChartTooltipFormatter = function () {
  if (isNilOrEmpty(this.points)) {
    return ''
  }

  const [offlinePoint, onlinePoint] = this.points

  return (
    offlinePoint &&
    onlinePoint &&
    `
    <div class="tide-div highcharts-tooltip-container highcharts-tooltip-container--bar-chart">
    <span class="tide-span highcharts-tooltip-header">${onlinePoint.x}</span>
      <div class="tide-div highcharts-tooltip-label-container">
        <div class="tide-div highcharts-coverage-tooltip-label">
          <div class="tooltip-team-availability-row">
            <div class="tooltip-agent-status-legend" style="background: ${onlinePoint.color}" />
            <div class="tooltip-team-availability-count">${onlinePoint.y} ${onlinePoint.series.name}</div>
          </div>
          <br />
          <div class="tooltip-team-availability-row">
            <div class="tooltip-agent-status-legend" style="background: ${offlinePoint.color}" />
            <div class="tooltip-team-availability-count">${offlinePoint.y} ${offlinePoint.series.name}</div>
          </div>
        </div>
      </div>
    </div>`
  )
}

export const donutChartTooltipFormatter = function (metric) {
  return function () {
    if (!this.point) {
      return ''
    }

    interface RTDPoint extends Highcharts.Point {
      rateLabel: (string) => string
      totalConvos: number
    }
    const point = this.point as RTDPoint

    const PointRow: React.FC = () => (
      <span className="highcharts-tooltip-label">
        <div className="tooltip-team-availability-row">
          <div
            className="tooltip-agent-status-legend"
            style={{ background: point.color as string }}
          />
          <div>{point.name}</div>
        </div>
        {point.y && (
          <div className="tooltip-value">
            {TypeFormats.RAW_HUMANIZED(point.y)}
          </div>
        )}
      </span>
    )

    const RateRow: React.FC = () =>
      point.y && point.totalConvos > 0 ? (
        <div className="tooltip-conversion-rate-text">
          {point.rateLabel(toPercentString(point.y / point.totalConvos, 1))}
        </div>
      ) : null
    return `
          <div class="tide-div highcharts-tooltip-container rtd-donut">
              <span class="tide-span highcharts-tooltip-header">${
                METRIC_LABEL_MAP[metric].barLabel
              }</span>
              <div class="tide-div highcharts-tooltip-label-container">
                  ${renderToStaticMarkup(<PointRow />)}
                  <br />
                  ${renderToStaticMarkup(<RateRow />)}
              </div>
          </div>
          `
  }
}

export const barLineChartTooltipFormatter = function () {
  return function () {
    if (!this.points) {
      return ''
    }

    const totalPoint = this.points[0]
    const ratePoint = this.points[1]

    const PointRow: React.FC = () => (
      <span className="highcharts-tooltip-label">
        <div className="tooltip-team-availability-row">
          <div
            className="tooltip-agent-status-legend"
            style={{ background: totalPoint.color as string }}
          />
          <div>{totalPoint.key}</div>
        </div>
        {totalPoint.y && (
          <div className="tooltip-value">
            {TypeFormats.RAW_HUMANIZED(totalPoint.y)}
          </div>
        )}
      </span>
    )

    const RateRow: React.FC = () =>
      ratePoint.y ? (
        <div className="tooltip-conversion-rate-text">{`${ratePoint.y}${ratePoint.key}`}</div>
      ) : null
    return `
          <div class="tide-div highcharts-tooltip-container rtd-donut">
              <span class="tide-span highcharts-tooltip-header">${this.x}</span>
              <div class="tide-div highcharts-tooltip-label-container">
                  ${renderToStaticMarkup(<PointRow />)}
                  <br />
                  ${renderToStaticMarkup(<RateRow />)}
              </div>
          </div>
          `
  }
}

export const missedChatsTooltipFormatter = function (
  tooltip,
  drilldownCallback: (point: any) => void
) {
  if (!tooltip.points) {
    return ''
  }
  const thisPoint = tooltip.points[0]

  const allDrilldownButtons = document.querySelectorAll(
    '[id^=missedChatBar-drilldown-viewmore]'
  )

  allDrilldownButtons[0]?.addEventListener('click', () => {
    thisPoint.point.teamData && drilldownCallback(thisPoint.point)
    allDrilldownButtons[0]?.removeEventListener('click', () => null)
  })

  let legendPoints = ''
  tooltip.points.forEach((point) => {
    legendPoints += `<div class="tooltip-missed-chat-row">
      <div>
        <div class="tooltip-missed-chat-legend" style="background: ${point.color}"></div>
        <div class="tooltip-missed-chat-label">${point.series.name}</div>
      </div>
      <div>
        <div class="tooltip-missed-chat-count">${point.y}</div>
      </div>
    </div>`
  })

  return `
  <div class="tide-div highcharts-tooltip-container highcharts-tooltip-container--bar-chart">
  <span class="tide-span highcharts-tooltip-header">${thisPoint.x}</span>
    <div class="tide-div highcharts-tooltip-label-container">
      <div class="tide-div highcharts-missed-chat-tooltip-label">
        ${legendPoints}
        <div class="tooltip-missed-chat-row">
          <div class="tooltip-team-availability-label">Total</div>
          <div class="tooltip-team-availability-count">${thisPoint?.total}</div>
        </div>
        <div class="tooltip-missed-chat-footer">
            <button
                id="missedChatBar-drilldown-viewmore-${thisPoint?.point.teamData.id}"
                class="tide-button tide-button--primary medium tide-button--tertiary"
            >
                View more
            </button>
        </div>
      </div>
    </div>
  </div>`
}

const getExperimentChartLabels = (
  flowAMetrics: ExperimentFlowMetrics,
  flowBMetrics: ExperimentFlowMetrics,
  index: number
) => {
  const pctChangeA = (flowAMetrics[index + 2] / flowAMetrics[index + 1]) * 100
  const pctChangeB = (flowBMetrics[index + 2] / flowBMetrics[index + 1]) * 100

  const pctChangeDelta = pctChangeA - pctChangeB

  return { pctChangeA, pctChangeB, pctChangeDelta }
}

export const createExperimentBarChartAreaTooltipFormatter = (
  flowAMetrics: ExperimentFlowMetrics,
  flowBMetrics: ExperimentFlowMetrics
): Highcharts.FormatterCallbackFunction<Highcharts.Point> => {
  return function () {
    const { index, category, series } = this
    const titlePrefixes = ['Sends', ...series.xAxis.categories]

    const title = `${titlePrefixes[index]} to ${category}`

    const { pctChangeA, pctChangeB, pctChangeDelta } = getExperimentChartLabels(
      flowAMetrics,
      flowBMetrics,
      index
    )

    const trendDirection =
      pctChangeDelta > 0 ? TrendDirection.NEGATIVE : TrendDirection.POSITIVE
    const showTrendIndicator = !!pctChangeDelta

    return renderToStaticMarkup(
      <Div className="highcharts-tooltip-container highcharts-tooltip-container--bar-chart highcharts-tooltip-container--experiment">
        <Div className="highcharts-tooltip-header">Conversions</Div>
        <Div className="highcharts-tooltip-label-container">
          <Span className="experiment-chart-tooltip-title">{title}</Span>
          <Span className="highcharts-tooltip-label experiment-chart-tooltip-label">
            <Span>
              <BotVariantIndicator />
            </Span>
            <Span>{TypeFormats.SAFE_PERCENT_WITH_DECIMAL(pctChangeA)}</Span>
          </Span>
          <Span className="highcharts-tooltip-label experiment-chart-tooltip-label">
            <Span>
              <BotVariantIndicator flowB />
            </Span>
            <Span>
              {TypeFormats.SAFE_PERCENT_WITH_DECIMAL(pctChangeB)}
              {showTrendIndicator && (
                <TrendIndicator
                  className="experiment-chart-trend-indicator"
                  direction={trendDirection}
                >
                  {TypeFormats.PERCENT_WITH_DECIMAL(Math.abs(pctChangeDelta))}
                </TrendIndicator>
              )}
            </Span>
          </Span>
        </Div>
      </Div>
    )
  }
}
export const createExperimentBarChartColumnTooltipFormatter = (
  flowAMetrics: number[],
  flowBMetrics: number[]
): Highcharts.FormatterCallbackFunction<Highcharts.Point> => {
  return function () {
    const { index, category } = this

    return renderToStaticMarkup(
      <Div className="highcharts-tooltip-container highcharts-tooltip-container--bar-chart highcharts-tooltip-container--experiment">
        <Div className="highcharts-tooltip-header">{category}</Div>
        <Div className="highcharts-tooltip-label-container">
          <Span className="highcharts-tooltip-label experiment-chart-tooltip-label">
            <Span>
              <BotVariantIndicator />
            </Span>
            <Span>{TypeFormats.RAW_HUMANIZED(flowAMetrics[index + 2])}</Span>
          </Span>
          <Span className="highcharts-tooltip-label experiment-chart-tooltip-label">
            <Span>
              <BotVariantIndicator flowB />
            </Span>
            <Span>{TypeFormats.RAW_HUMANIZED(flowBMetrics[index + 2])}</Span>
          </Span>
        </Div>
      </Div>
    )
  }
}

export const createExperimentBarChartAnnotationFormatter = (
  flowAMetrics: ExperimentFlowMetrics,
  flowBMetrics: ExperimentFlowMetrics,
  isFlowA?: boolean
): Highcharts.FormatterCallbackFunction<Highcharts.Point> => {
  return function () {
    const { x: index } = this
    const { pctChangeA, pctChangeB, pctChangeDelta } = getExperimentChartLabels(
      flowAMetrics,
      flowBMetrics,
      index
    )

    const displayPercent = isFlowA ? pctChangeA : pctChangeB

    const trendDirection =
      pctChangeDelta > 0 ? TrendDirection.NEGATIVE : TrendDirection.POSITIVE

    const showTrendIndicator = !isFlowA && !!pctChangeDelta
    return renderToStaticMarkup(
      <div
        className={cx(
          'experiment-chart-annotation',
          showTrendIndicator && `experiment-chart-annotation--${trendDirection}`
        )}
      >
        {TypeFormats.SAFE_PERCENT_WITH_DECIMAL(displayPercent)}
        {showTrendIndicator && (
          <TrendIndicator
            className="experiment-chart-trend-indicator"
            direction={trendDirection}
          />
        )}
      </div>
    )
  }
}

export const getHumanizedShortDuration = (duration) => {
  const momentDuration = moment.duration(duration)
  const days = momentDuration.days()
  const hours = momentDuration.hours()
  const minutes = momentDuration.minutes()
  const seconds = momentDuration.seconds()
  if (days) {
    return hours ? `${days}d ${hours}h` : `${days}d`
  }
  if (hours) {
    return minutes ? `${hours}h ${minutes}m` : `${hours}h`
  }
  if (minutes) {
    return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`
  }
  return seconds ? `${seconds}s` : 0
}

export const truncateLabel = function () {
  if (this.value.length > 20) {
    return `${this.value.substring(0, 24)}…`
  } else {
    return this.value
  }
}

export const lineBreakLabel = function () {
  if (this.value.length > 20) {
    const words = this.value.split(' ')
    const middle = Math.ceil(words.length / 2)
    words.splice(middle, 0, '<br />')
    return words.join(' ')
  } else {
    return this.value
  }
}
